/**
 * Logique et ensembles — logique pure (Boîte à outils, 2nde → prépa).
 *
 * Trois regards sur le même raisonnement :
 *  - des ENSEMBLES dans un univers fini E = {1, …, 12} (diagramme de Venn) :
 *    complémentaire, intersection, réunion, différence, inclusion ;
 *  - des PROPOSITIONS P, Q (R en prépa) : tables de vérité, deux formules
 *    comparées ligne à ligne (contraposée, réciproque, lois de Morgan…) ;
 *  - des QUANTIFICATEURS sur un ensemble fini écrit en entier : « pour tout »,
 *    « il existe », contre-exemple, négation ; deux variables en prépa (l'ordre
 *    des quantificateurs).
 * Tout est fini et vérifié élément par élément : jamais de « semble ».
 */

/* ------------------------------------------------------------------ */
/* ensembles                                                           */
/* ------------------------------------------------------------------ */

export const UNIVERSE = Array.from({ length: 12 }, (_, i) => i + 1);

export interface SetDef {
  id: string;
  /** « les nombres pairs » */
  name: string;
  has: (n: number) => boolean;
}

const isPrime = (n: number) => {
  if (n < 2) return false;
  for (let d = 2; d * d <= n; d++) if (n % d === 0) return false;
  return true;
};

export const SET_DEFS: readonly SetDef[] = [
  { id: 'even', name: 'les nombres pairs', has: (n) => n % 2 === 0 },
  { id: 'odd', name: 'les nombres impairs', has: (n) => n % 2 === 1 },
  { id: 'mul3', name: 'les multiples de 3', has: (n) => n % 3 === 0 },
  { id: 'mul4', name: 'les multiples de 4', has: (n) => n % 4 === 0 },
  { id: 'prime', name: 'les nombres premiers', has: isPrime },
  { id: 'square', name: 'les carrés parfaits', has: (n) => Number.isInteger(Math.sqrt(n)) },
  { id: 'div12', name: 'les diviseurs de 12', has: (n) => 12 % n === 0 },
  { id: 'le6', name: 'les nombres inférieurs ou égaux à 6', has: (n) => n <= 6 },
  { id: 'gt8', name: 'les nombres strictement supérieurs à 8', has: (n) => n > 8 },
];

export const SET_NAMES = ['A', 'B', 'C'] as const;

export type SetExpr =
  | { k: 'set'; i: 0 | 1 | 2 }
  | { k: 'comp'; a: SetExpr }
  | { k: 'inter' | 'union' | 'diff'; a: SetExpr; b: SetExpr };

const S = (i: 0 | 1 | 2): SetExpr => ({ k: 'set', i });
const comp = (a: SetExpr): SetExpr => ({ k: 'comp', a });
const inter = (a: SetExpr, b: SetExpr): SetExpr => ({ k: 'inter', a, b });
const union = (a: SetExpr, b: SetExpr): SetExpr => ({ k: 'union', a, b });
const diff = (a: SetExpr, b: SetExpr): SetExpr => ({ k: 'diff', a, b });

/** expressions proposées : 2 ensembles, puis celles qui demandent C */
export const SET_EXPRS_2: readonly SetExpr[] = [
  S(0),
  S(1),
  comp(S(0)),
  inter(S(0), S(1)),
  union(S(0), S(1)),
  diff(S(0), S(1)),
  diff(S(1), S(0)),
  comp(inter(S(0), S(1))),
  union(comp(S(0)), comp(S(1))),
  comp(union(S(0), S(1))),
  inter(comp(S(0)), comp(S(1))),
];
export const SET_EXPRS_3: readonly SetExpr[] = [
  inter(inter(S(0), S(1)), S(2)),
  union(union(S(0), S(1)), S(2)),
  inter(union(S(0), S(1)), S(2)),
  union(inter(S(0), S(2)), inter(S(1), S(2))),
  union(S(0), inter(S(1), S(2))),
  inter(union(S(0), S(1)), union(S(0), S(2))),
];

/** région d'un élément : bit i = il appartient au i-ème ensemble */
export const regionOf = (n: number, sets: readonly SetDef[]) =>
  sets.reduce((m, s, i) => (s.has(n) ? m | (1 << i) : m), 0);

export function inRegion(e: SetExpr, mask: number): boolean {
  switch (e.k) {
    case 'set':
      return (mask & (1 << e.i)) !== 0;
    case 'comp':
      return !inRegion(e.a, mask);
    case 'inter':
      return inRegion(e.a, mask) && inRegion(e.b, mask);
    case 'union':
      return inRegion(e.a, mask) || inRegion(e.b, mask);
    case 'diff':
      return inRegion(e.a, mask) && !inRegion(e.b, mask);
  }
}

export const elementsOf = (e: SetExpr, sets: readonly SetDef[]) =>
  UNIVERSE.filter((n) => inRegion(e, regionOf(n, sets)));

/** combien d'ensembles l'expression utilise (2 ou 3) */
export const arity = (e: SetExpr): number =>
  e.k === 'set' ? e.i + 1 : e.k === 'comp' ? arity(e.a) : Math.max(arity(e.a), arity(e.b));

const isAtom = (e: SetExpr) => e.k === 'set' || e.k === 'comp';

/** LaTeX : « \overline{A} \cup \overline{B} », « (A \cup B) \cap C » */
export function setTex(e: SetExpr): string {
  const wrap = (x: SetExpr) => (isAtom(x) ? setTex(x) : `(${setTex(x)})`);
  switch (e.k) {
    case 'set':
      return SET_NAMES[e.i];
    case 'comp':
      return `\\overline{${setTex(e.a)}}`;
    case 'inter':
      return `${wrap(e.a)} \\cap ${wrap(e.b)}`;
    case 'union':
      return `${wrap(e.a)} \\cup ${wrap(e.b)}`;
    case 'diff':
      return `${wrap(e.a)} \\setminus ${wrap(e.b)}`;
  }
}

/** lecture pour le lecteur d'écran : « complémentaire de A union B » */
export function setSpeech(e: SetExpr): string {
  const wrap = (x: SetExpr) => (isAtom(x) ? setSpeech(x) : `(${setSpeech(x)})`);
  switch (e.k) {
    case 'set':
      return SET_NAMES[e.i];
    case 'comp':
      return `complémentaire de ${wrap(e.a)}`;
    case 'inter':
      return `${wrap(e.a)} inter ${wrap(e.b)}`;
    case 'union':
      return `${wrap(e.a)} union ${wrap(e.b)}`;
    case 'diff':
      return `${wrap(e.a)} privé de ${wrap(e.b)}`;
  }
}

/** ce que veut dire « x ∈ … » : « x ∈ A et x ∉ B », en LaTeX */
export function memberTex(e: SetExpr): string {
  const inner = (x: SetExpr): string => {
    switch (x.k) {
      case 'set':
        return `x \\in ${SET_NAMES[x.i]}`;
      case 'comp':
        return x.a.k === 'set' ? `x \\notin ${SET_NAMES[x.a.i]}` : `\\text{non}\\,(${inner(x.a)})`;
      case 'inter':
        return `${par(x.a)} \\text{ et } ${par(x.b)}`;
      case 'union':
        return `${par(x.a)} \\text{ ou } ${par(x.b)}`;
      case 'diff':
        return `${par(x.a)} \\text{ et } ${par(comp(x.b))}`;
    }
  };
  const par = (x: SetExpr) =>
    x.k === 'set' || (x.k === 'comp' && x.a.k === 'set') ? inner(x) : `(${inner(x)})`;
  return `x \\in ${setTex(e)} \\iff ${inner(e)}`;
}

/** les deux côtés de memberTex, pour les poser sur deux lignes */
export function memberParts(e: SetExpr): { lhs: string; rhs: string } {
  const t = memberTex(e);
  const k = t.indexOf(' \\iff ');
  return { lhs: t.slice(0, k), rhs: t.slice(k + ' \\iff '.length) };
}

/** {2, 4, 6} ou ∅ */
export const listTex = (xs: readonly number[]) =>
  xs.length ? `\\{${xs.join(',\\ ')}\\}` : '\\varnothing';

/** A ⊂ B ? et sinon un contre-exemple (le plus petit) */
export function inclusion(
  a: SetExpr,
  b: SetExpr,
  sets: readonly SetDef[],
): { holds: boolean; witness: number | null } {
  const w = UNIVERSE.find((n) => {
    const m = regionOf(n, sets);
    return inRegion(a, m) && !inRegion(b, m);
  });
  return { holds: w === undefined, witness: w ?? null };
}

/** deux expressions égales sur TOUTES les régions (identité), ou seulement sur E ? */
export function sameSet(a: SetExpr, b: SetExpr, sets: readonly SetDef[]) {
  const n = sets.length;
  let identity = true;
  for (let m = 0; m < 1 << n; m++) if (inRegion(a, m) !== inRegion(b, m)) identity = false;
  const ea = elementsOf(a, sets);
  const eb = elementsOf(b, sets);
  const here = ea.length === eb.length && ea.every((x, i) => x === eb[i]);
  return { identity, here };
}

/* ------------------------------------------------------------------ */
/* propositions et tables de vérité                                    */
/* ------------------------------------------------------------------ */

export type Var = 'P' | 'Q' | 'R';
export type Prop =
  | { k: 'var'; v: Var }
  | { k: 'not'; a: Prop }
  | { k: 'and' | 'or' | 'imp' | 'iff'; a: Prop; b: Prop };

const V = (v: Var): Prop => ({ k: 'var', v });
const not = (a: Prop): Prop => ({ k: 'not', a });
const and = (a: Prop, b: Prop): Prop => ({ k: 'and', a, b });
const or = (a: Prop, b: Prop): Prop => ({ k: 'or', a, b });
const imp = (a: Prop, b: Prop): Prop => ({ k: 'imp', a, b });
const iff = (a: Prop, b: Prop): Prop => ({ k: 'iff', a, b });
const [P, Qv, R] = [V('P'), V('Q'), V('R')];

export function evalProp(p: Prop, env: Record<Var, boolean>): boolean {
  switch (p.k) {
    case 'var':
      return env[p.v];
    case 'not':
      return !evalProp(p.a, env);
    case 'and':
      return evalProp(p.a, env) && evalProp(p.b, env);
    case 'or':
      return evalProp(p.a, env) || evalProp(p.b, env);
    case 'imp':
      return !evalProp(p.a, env) || evalProp(p.b, env);
    case 'iff':
      return evalProp(p.a, env) === evalProp(p.b, env);
  }
}

const OP_TEXT = { and: 'et', or: 'ou', imp: '⇒', iff: '⇔' } as const;

/** « non P ou Q », « (P ⇒ Q) et (Q ⇒ P) », « non (P et Q) » */
export function propText(p: Prop): string {
  const wrap = (x: Prop) => (x.k === 'var' || x.k === 'not' ? propText(x) : `(${propText(x)})`);
  if (p.k === 'var') return p.v;
  if (p.k === 'not') return `non ${wrap(p.a)}`;
  return `${wrap(p.a)} ${OP_TEXT[p.k]} ${wrap(p.b)}`;
}

export const varsOf = (p: Prop): Var[] => {
  const s = new Set<Var>();
  const walk = (x: Prop) => {
    if (x.k === 'var') s.add(x.v);
    else if (x.k === 'not') walk(x.a);
    else {
      walk(x.a);
      walk(x.b);
    }
  };
  walk(p);
  return (['P', 'Q', 'R'] as const).filter((v) => s.has(v));
};

/** sous-formules (sans les variables), de la plus petite à la formule entière */
export function subformulas(p: Prop): Prop[] {
  const out: Prop[] = [];
  const seen = new Set<string>();
  const walk = (x: Prop) => {
    if (x.k === 'var') return;
    if (x.k === 'not') walk(x.a);
    else {
      walk(x.a);
      walk(x.b);
    }
    const t = propText(x);
    if (!seen.has(t)) {
      seen.add(t);
      out.push(x);
    }
  };
  walk(p);
  return out;
}

/** les lignes de la table : V puis F, comme au lycée (V V, V F, F V, F F) */
export function rows(vars: readonly Var[]): Array<Record<Var, boolean>> {
  const n = vars.length;
  return Array.from({ length: 1 << n }, (_, r) => {
    const env = { P: false, Q: false, R: false } as Record<Var, boolean>;
    vars.forEach((v, i) => {
      env[v] = ((r >> (n - 1 - i)) & 1) === 0;
    });
    return env;
  });
}

export interface Comparison {
  id: string;
  /** nom court du bouton */
  label: string;
  left: Prop;
  right: Prop;
  /** ce qu'on retient, quand elles sont équivalentes ou non */
  lesson: string;
  /** rang de niveau à partir duquel la proposer */
  minRank?: number;
}

export const COMPARISONS: readonly Comparison[] = [
  {
    id: 'contrapositive',
    label: 'Contraposée',
    left: imp(P, Qv),
    right: imp(not(Qv), not(P)),
    lesson:
      'Une implication et sa contraposée sont équivalentes : pour prouver P ⇒ Q, on peut prouver non Q ⇒ non P.',
  },
  {
    id: 'converse',
    label: 'Réciproque',
    left: imp(P, Qv),
    right: imp(Qv, P),
    lesson:
      'Une implication et sa réciproque ne sont PAS équivalentes : l’une peut être vraie et l’autre fausse.',
  },
  {
    id: 'definition',
    label: 'P ⇒ Q ⇔ non P ou Q',
    left: imp(P, Qv),
    right: or(not(P), Qv),
    lesson:
      'P ⇒ Q n’est fausse que dans un cas : P vraie et Q fausse. C’est exactement « non P ou Q ».',
  },
  {
    id: 'negation-imp',
    label: 'Nier une implication',
    left: not(imp(P, Qv)),
    right: and(P, not(Qv)),
    lesson: 'Le contraire de « P ⇒ Q » est « P et non Q » : un cas où P est vraie mais pas Q.',
  },
  {
    id: 'morgan-and',
    label: 'Morgan : non (P et Q)',
    left: not(and(P, Qv)),
    right: or(not(P), not(Qv)),
    lesson: 'Le contraire de « P et Q » est « non P ou non Q » : le « et » devient « ou ».',
  },
  {
    id: 'morgan-or',
    label: 'Morgan : non (P ou Q)',
    left: not(or(P, Qv)),
    right: and(not(P), not(Qv)),
    lesson: 'Le contraire de « P ou Q » est « non P et non Q » : le « ou » devient « et ».',
  },
  {
    id: 'equivalence',
    label: 'Équivalence',
    left: iff(P, Qv),
    right: and(imp(P, Qv), imp(Qv, P)),
    lesson:
      'P ⇔ Q, c’est l’implication ET sa réciproque : on prouve souvent les deux sens séparément.',
  },
  {
    id: 'wrong-negation',
    label: 'Piège : non (P et Q)',
    left: not(and(P, Qv)),
    right: and(not(P), not(Qv)),
    lesson:
      'Erreur classique : « non (P et Q) » n’est pas « non P et non Q ». Il suffit qu’UNE des deux soit fausse.',
  },
  {
    id: 'distributivity',
    label: 'Distributivité',
    left: and(P, or(Qv, R)),
    right: or(and(P, Qv), and(P, R)),
    lesson: '« et » se distribue sur « ou », comme ∩ sur ∪.',
    minRank: 8,
  },
  {
    id: 'exportation',
    label: '(P et Q) ⇒ R',
    left: imp(and(P, Qv), R),
    right: imp(P, imp(Qv, R)),
    lesson: 'Supposer P et Q pour prouver R, c’est supposer P, puis Q, puis prouver R.',
    minRank: 8,
  },
];

/** lignes où les deux formules diffèrent */
export function compare(c: Comparison): {
  vars: Var[];
  table: Array<{ env: Record<Var, boolean>; left: boolean; right: boolean }>;
  equivalent: boolean;
} {
  const vars = [...new Set([...varsOf(c.left), ...varsOf(c.right)])].sort() as Var[];
  const table = rows(vars).map((env) => ({
    env,
    left: evalProp(c.left, env),
    right: evalProp(c.right, env),
  }));
  return { vars, table, equivalent: table.every((r) => r.left === r.right) };
}

/* ------------------------------------------------------------------ */
/* quantificateurs                                                     */
/* ------------------------------------------------------------------ */

export interface Domain {
  id: string;
  /** « E = {0, 1, …, 10} » */
  tex: string;
  values: readonly number[];
}

export const DOMAINS: readonly Domain[] = [
  {
    id: 'N',
    tex: 'E = \\{0,\\ 1,\\ \\dots,\\ 10\\}',
    values: Array.from({ length: 11 }, (_, i) => i),
  },
  {
    id: 'Z',
    tex: 'E = \\{-5,\\ -4,\\ \\dots,\\ 5\\}',
    values: Array.from({ length: 11 }, (_, i) => i - 5),
  },
];

export interface Pred {
  id: string;
  /** LaTeX de P(n) et de non P(n) */
  tex: string;
  neg: string;
  /** texte simple (boutons) */
  plain: string;
  test: (n: number) => boolean;
}

export const PREDS: readonly Pred[] = [
  { id: 'sq-ge', plain: 'n² ≥ n', tex: 'n^2 \\geq n', neg: 'n^2 < n', test: (n) => n * n >= n },
  { id: 'sq-gt', plain: 'n² > n', tex: 'n^2 > n', neg: 'n^2 \\leq n', test: (n) => n * n > n },
  {
    id: 'even',
    plain: 'n est pair',
    tex: 'n \\text{ est pair}',
    neg: 'n \\text{ est impair}',
    test: (n) => n % 2 === 0,
  },
  { id: 'twice', plain: '2n > n', tex: '2n > n', neg: '2n \\leq n', test: (n) => 2 * n > n },
  { id: 'sq4', plain: 'n² = 4', tex: 'n^2 = 4', neg: 'n^2 \\neq 4', test: (n) => n * n === 4 },
  { id: 'cube', plain: 'n³ = n', tex: 'n^3 = n', neg: 'n^3 \\neq n', test: (n) => n ** 3 === n },
  {
    id: 'consecutive',
    plain: 'n(n + 1) est pair',
    tex: 'n(n + 1) \\text{ est pair}',
    neg: 'n(n + 1) \\text{ est impair}',
    test: (n) => (n * (n + 1)) % 2 === 0,
  },
  { id: 'gt7', plain: 'n > 7', tex: 'n > 7', neg: 'n \\leq 7', test: (n) => n > 7 },
];

export type Quant = 'all' | 'exists';

export function quantVerdict(
  q: Quant,
  pred: Pred,
  dom: Domain,
): { truth: boolean; witness: number | null; marks: boolean[] } {
  const marks = dom.values.map(pred.test);
  if (q === 'all') {
    const i = marks.indexOf(false);
    return { truth: i < 0, witness: i < 0 ? null : dom.values[i]!, marks };
  }
  const i = marks.indexOf(true);
  return { truth: i >= 0, witness: i < 0 ? null : dom.values[i]!, marks };
}

export const quantTex = (q: Quant, pred: Pred, negated = false) =>
  q === 'all'
    ? `\\forall n \\in E,\\ ${negated ? pred.neg : pred.tex}`
    : `\\exists n \\in E,\\ ${negated ? pred.neg : pred.tex}`;

/* deux variables (prépa) : l'ordre des quantificateurs */

export interface Pred2 {
  id: string;
  plain: string;
  tex: string;
  neg: string;
  test: (x: number, y: number) => boolean;
  /** remarque quand un verdict tient au fait que F est fini */
  note?: Partial<Record<Order, string>>;
}

export const PREDS2: readonly Pred2[] = [
  {
    id: 'opp',
    plain: 'x + y = 0',
    tex: 'x + y = 0',
    neg: 'x + y \\neq 0',
    test: (x, y) => x + y === 0,
  },
  {
    id: 'le',
    plain: 'x ≤ y',
    tex: 'x \\leq y',
    neg: 'x > y',
    test: (x, y) => x <= y,
    note: {
      EA: 'Vraie ici seulement parce que F a un plus grand élément (3). Sur ℤ, il n’y a pas de plus grand entier : la même phrase y est fausse.',
    },
  },
  {
    id: 'sq-eq',
    plain: 'x = y²',
    tex: 'x = y^2',
    neg: 'x \\neq y^2',
    test: (x, y) => x === y * y,
  },
  {
    id: 'prod0',
    plain: 'xy = 0',
    tex: 'xy = 0',
    neg: 'xy \\neq 0',
    test: (x, y) => x * y === 0,
  },
  {
    id: 'sq',
    plain: 'y² ≥ x',
    tex: 'y^2 \\geq x',
    neg: 'y^2 < x',
    test: (x, y) => y * y >= x,
  },
];

export const GRID = [-3, -2, -1, 0, 1, 2, 3] as const;

export type Order = 'AE' | 'EA' | 'AA' | 'EE';

export const ORDER_TEX: Record<Order, string> = {
  AE: '\\forall x \\in F,\\ \\exists y \\in F,\\ ',
  EA: '\\exists y \\in F,\\ \\forall x \\in F,\\ ',
  AA: '\\forall x \\in F,\\ \\forall y \\in F,\\ ',
  EE: '\\exists x \\in F,\\ \\exists y \\in F,\\ ',
};

/** négation : on échange ∀ et ∃ et on nie la propriété */
export const NEG_ORDER: Record<Order, string> = {
  AE: '\\exists x \\in F,\\ \\forall y \\in F,\\ ',
  EA: '\\forall y \\in F,\\ \\exists x \\in F,\\ ',
  AA: '\\exists x \\in F,\\ \\exists y \\in F,\\ ',
  EE: '\\forall x \\in F,\\ \\forall y \\in F,\\ ',
};

const m = (n: number) => String(n).replace('-', '−');

export function order2(
  o: Order,
  p: Pred2,
): {
  truth: boolean;
  why: string;
  /** lignes (x) et colonnes (y) à montrer, et une case témoin */
  rows: number[];
  cols: number[];
  cell: [number, number] | null;
  note: string | null;
} {
  const t = (x: number, y: number) => p.test(x, y);
  const note = p.note?.[o] ?? null;
  // lignes = x, colonnes = y
  const fullCols = GRID.filter((y) => GRID.every((x) => t(x, y)));
  const cells = GRID.flatMap((x) => GRID.map((y) => [x, y] as [number, number]));
  switch (o) {
    case 'AE': {
      const bad = GRID.find((x) => !GRID.some((y) => t(x, y)));
      return {
        truth: bad === undefined,
        why:
          bad === undefined
            ? 'Chaque ligne x contient au moins une case vraie : y peut changer avec x.'
            : `La ligne x = ${m(bad)} n’a aucune case vraie : pour ce x, aucun y ne convient.`,
        rows: bad === undefined ? [] : [bad],
        cols: [],
        cell: null,
        note,
      };
    }
    case 'EA':
      return {
        truth: fullCols.length > 0,
        why: fullCols.length
          ? `La colonne y = ${m(fullCols[0]!)} est vraie en entier : le MÊME y convient à tous les x.`
          : 'Aucune colonne n’est vraie en entier : pas de y qui convienne à tous les x à la fois.',
        rows: [],
        cols: fullCols.slice(0, 1),
        cell: null,
        note,
      };
    case 'AA': {
      const bad = cells.find(([x, y]) => !t(x, y)) ?? null;
      return {
        truth: bad === null,
        why: bad
          ? `La case x = ${m(bad[0])}, y = ${m(bad[1])} est fausse : c’est un contre-exemple.`
          : 'Toutes les cases sont vraies.',
        rows: [],
        cols: [],
        cell: bad,
        note,
      };
    }
    case 'EE': {
      const good = cells.find(([x, y]) => t(x, y)) ?? null;
      return {
        truth: good !== null,
        why: good
          ? `La case x = ${m(good[0])}, y = ${m(good[1])} est vraie : un exemple suffit.`
          : 'Aucune case n’est vraie.',
        rows: [],
        cols: [],
        cell: good,
        note,
      };
    }
  }
}
